import React from 'react';
import { Box, Typography } from '@mui/material';
import { CT } from './chatTheme';

/**
 * Messenger-style three-dot animation. The dots are staggered so they read as a
 * wave rather than a blink, and the whole row animates in from below so it
 * doesn't pop. Respects prefers-reduced-motion.
 */
const TypingIndicator: React.FC<{ label: string }> = ({ label }) => (
  <Box
    aria-live="polite"
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 2,
      py: 0.75,
      '@keyframes sfTypingBounce': {
        '0%, 60%, 100%': { transform: 'translateY(0)', opacity: 0.45 },
        '30%': { transform: 'translateY(-4px)', opacity: 1 },
      },
      '@keyframes sfTypingIn': {
        from: { opacity: 0, transform: 'translateY(6px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
      },
      animation: 'sfTypingIn .18s ease-out',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    }}
  >
    {/* Dots sit in a bubble so they read as an in-flight message. */}
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        px: 1.25,
        py: 0.9,
        borderRadius: '14px 14px 14px 4px',
        bgcolor: CT.hairline,
      }}
    >
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: CT.muted,
            animation: 'sfTypingBounce 1.2s infinite ease-in-out',
            animationDelay: `${i * 0.18}s`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0.6 },
          }}
        />
      ))}
    </Box>
    <Typography sx={{ fontSize: 11.5, color: CT.muted, fontWeight: 600 }} noWrap>
      {label}
    </Typography>
  </Box>
);

export default TypingIndicator;
